import { X, Keyboard } from 'lucide-react';
import {
  useActiveShortcuts,
  type ShortcutContext,
} from '@/hooks/useKeyboardShortcuts';
import { ShortcutIndicator } from './ShortcutIndicator';

interface ShortcutHelpOverlayProps {
  isOpen: boolean;
  onClose: () => void;
}

export function ShortcutHelpOverlay({
  isOpen,
  onClose,
}: ShortcutHelpOverlayProps) {
  const activeShortcuts = useActiveShortcuts();

  if (!isOpen) return null;

  // Group shortcuts by category
  const groupedShortcuts = new Map<string, ShortcutContext[]>();

  activeShortcuts.forEach((context) => {
    const categoryMap = new Map<string, typeof context.shortcuts>();

    context.shortcuts.forEach((shortcut) => {
      const category = shortcut.category || 'General';
      const existing = categoryMap.get(category) || [];
      existing.push(shortcut);
      categoryMap.set(category, existing);
    });

    categoryMap.forEach((shortcuts, category) => {
      const existing = groupedShortcuts.get(category) || [];
      existing.push({ context: context.context, shortcuts });
      groupedShortcuts.set(category, existing);
    });
  });

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-slate-100 rounded-lg">
              <Keyboard className="h-5 w-5 text-slate-700" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                Keyboard Shortcuts
              </h2>
              <p className="text-sm text-gray-500">
                Available shortcuts for the current screen
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[70vh]">
          {groupedShortcuts.size === 0 ? (
            <div className="text-center py-12">
              <Keyboard className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No Shortcuts Available
              </h3>
              <p className="text-gray-500">
                No keyboard shortcuts are available for the current screen.
              </p>
            </div>
          ) : (
            <div className="grid gap-8">
              {Array.from(groupedShortcuts.entries()).map(
                ([category, contexts]) => (
                  <div key={category}>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      {category}
                    </h3>
                    <div className="grid gap-4">
                      {contexts.map((context) => (
                        <div key={context.context}>
                          {contexts.length > 1 && (
                            <h4 className="text-sm font-medium text-gray-700 mb-2 uppercase tracking-wide">
                              {context.context}
                            </h4>
                          )}
                          <div className="grid gap-2">
                            {context.shortcuts
                              .filter((shortcut) => !shortcut.disabled)
                              .map((shortcut) => (
                                <div
                                  key={`${context.context}-${shortcut.description}`}
                                  className="flex items-center justify-between py-2 px-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors"
                                >
                                  <span className="text-sm text-gray-700">
                                    {shortcut.description}
                                  </span>
                                  <ShortcutIndicator
                                    shortcut={shortcut}
                                    variant="compact"
                                  />
                                </div>
                              ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 p-4 bg-gray-50">
          <div className="flex items-center justify-between text-sm text-gray-600">
            <span className="flex items-center gap-1">
              Press{' '}
              <ShortcutIndicator
                shortcut={{ key: '?', modifiers: { shift: true } }}
                variant="compact"
              />{' '}
              to toggle this help
            </span>
            <span className="flex items-center gap-1">
              Press{' '}
              <ShortcutIndicator
                shortcut={{ key: 'Escape' }}
                variant="compact"
              />{' '}
              to close
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
